import { z } from 'zod';
/**
 * Lieferanten-Stammdaten. Siehe `docs/02_DOMAIN_MODEL.md` §4.1 und §5.
 *
 * **Sichtbarkeitsregel (D17), die dieses Modul strukturell durchsetzt:** Firmenname und Profil
 * sind vor dem Zuschlag sichtbar, **Kontaktdaten und exakte Adresse nicht**. Deshalb gibt es
 * {@link SupplierPublicViewSchema} als eigenes Schema statt als Laufzeit-Teilmenge — ein neu
 * ergänztes Feld auf dem Stammdatensatz kann so nicht versehentlich nach außen durchrutschen.
 *
 * Dieselbe Logik gilt in die andere Richtung: {@link SupplierProfileInputSchema} ist die
 * **Einschlussliste dessen, was ein Lieferant an sich selbst ändern darf**. `status`,
 * `compliance` und `commissionRateBp` stehen bewusst nicht darin — wer seinen eigenen
 * Freigabestatus setzen kann, macht die Verifizierung zur Zierde.
 */
/** Freigabestatus. Nur `verified` darf Ausschreibungen empfangen. */
export const SupplierStatusSchema = z.enum([
    /** Vom Admin angelegt oder selbst registriert, noch nichts geprüft. */
    'pending',
    /** Unterlagen liegen vor, Prüfung läuft. */
    'in_review',
    /** Geprüft und freigeschaltet — empfängt Ausschreibungen. */
    'verified',
    /** Vom Admin gesperrt (Qualität, Zahlungsverzug). Bestandsvorgänge laufen weiter. */
    'suspended',
    /** Abgelehnt oder ausgeschieden. */
    'rejected',
]);
/**
 * Der einzige Status, der Ausschreibungen empfängt.
 *
 * Als Konstante und nicht als String-Literal an fünf Stellen: Das Matching (Phase 5), der
 * Angebots-Handler und die Admin-Liste fragen dasselbe ab, und ein Tippfehler in einem der
 * drei wäre ein Lieferant, der still keine Anfragen mehr bekommt.
 */
export const SUPPLIER_ACTIVE_STATUS = 'verified';
/**
 * Erlaubte Statuswechsel. Die Matrix steht hier und nicht im Handler, damit Frontend und
 * Backend dieselbe Antwort auf „welcher Knopf darf jetzt gedrückt werden" geben.
 *
 * `rejected` ist bewusst **kein** Endzustand: Eine Ablehnung wegen fehlender Unterlagen wird
 * regelmäßig geheilt, und ein Betrieb, der nachreicht, soll nicht neu angelegt werden
 * müssen.
 */
export const SUPPLIER_STATUS_TRANSITIONS = {
    pending: ['in_review', 'verified', 'rejected'],
    in_review: ['verified', 'rejected', 'pending'],
    verified: ['suspended', 'rejected'],
    suspended: ['verified', 'rejected'],
    rejected: ['pending', 'in_review'],
};
export function canTransitionSupplierStatus(from, to) {
    // eslint-disable-next-line security/detect-object-injection -- `from` ist ein SupplierStatus, also per Typ ein Schlüssel der Matrix
    return SUPPLIER_STATUS_TRANSITIONS[from].includes(to);
}
/** Art der Leistung — steuert das Matching (D8) und den USt-Satz-Vorschlag. */
export const ServiceTypeSchema = z.enum([
    'fingerfood',
    'buffet',
    'menue_serviert',
    'business_lunch',
    'fruehstueck',
    'kaffeepause',
    'getraenke',
    'foodtruck',
    'grill',
    'personal_service',
]);
/** Küchenstil. Bewusst grob — Feinsortierung macht das Modell (D8), nicht die Taxonomie. */
export const CuisineSchema = z.enum([
    'deutsch',
    'international',
    'italienisch',
    'franzoesisch',
    'mediterran',
    'asiatisch',
    'orientalisch',
    'indisch',
    'amerikanisch',
    'levantinisch',
]);
/** Ernährungsformen, die ein Lieferant zuverlässig bedienen kann. */
export const DietaryOptionSchema = z.enum([
    'vegetarisch',
    'vegan',
    'glutenfrei',
    'laktosefrei',
    'halal',
    'koscher',
    'nussfrei',
    'schweinefleischfrei',
]);
/**
 * Zertifikate, die ein Betrieb führt.
 *
 * Getrennt von {@link SupplierComplianceSchema}: Hier stehen **Auszeichnungen**, die der
 * Lieferant selbst angibt und die im Profil werben dürfen. Dort stehen die **Nachweise**,
 * die caterinx geprüft hat und die über die Freischaltung entscheiden. Beides zu vermischen
 * hieße, eine Selbstauskunft wie eine Prüfung aussehen zu lassen.
 */
export const SupplierCertificationSchema = z.enum([
    'bio_eu',
    'haccp',
    'ifs_food',
    'iso_22000',
    'msc',
    'demeter',
    'regional',
    'klimaneutral',
]);
/**
 * Liefergebiet als PLZ-Präfix.
 *
 * `plz2` ist der zweistellige Präfix ('40' deckt 40xxx ab) — er ist der Partitionsschlüssel
 * des Regions-Items (`AREA#<plz2>`) und damit die Grundlage des deterministischen Hard-Filters
 * im Matching. Je Präfix ein eigenes DynamoDB-Item, damit die Abfrage "wer liefert nach 40?"
 * ein einzelner GSI1-Query bleibt.
 */
export const ServiceAreaSchema = z.object({
    plz2: z.string().regex(/^\d{2}$/, 'PLZ-Präfix besteht aus genau zwei Ziffern'),
    /** Aufschlag für Anfahrt in dieses Gebiet, in Cent. 0 = keine Anfahrtskosten. */
    travelSurchargeCents: z.int().min(0).max(1_000_000).default(0),
});
/**
 * Obergrenze für Liefergebiete je Lieferant.
 *
 * Deutschland hat 95 belegte Zweisteller-Präfixe; wer alle anhakt, liefert nicht überall,
 * sondern hat den Dialog nicht gelesen. Die Grenze ist zugleich ein Kostendeckel: Jedes
 * Gebiet ist ein eigenes Item im GSI1 und damit ein Treffer mehr in jedem Matching-Lauf.
 */
export const MAX_SERVICE_AREAS = 40;
/**
 * Der Partitionsschlüssel für „liefert deutschlandweit" — `AREA#ALL`.
 *
 * **Warum ein Sentinel und nicht 95 Items:** Der Regelfall unter Caterern ist der
 * bundesweite Betrieb, nicht die Regionalküche. Alle belegten Zweisteller-Präfixe zu
 * schreiben wären 95 Items je Lieferant, ein Vielfaches von {@link MAX_SERVICE_AREAS} und
 * mehr, als eine DynamoDB-Transaktion (100 Operationen) trägt, sobald noch Löschungen
 * dazukommen.
 *
 * Ein einzelnes Item in einer eigenen Partition kostet stattdessen genau **einen zweiten
 * GSI1-Query** je Matching-Lauf: „wer liefert nach 40?" plus „wer liefert überall?". Der
 * Wert ist bewusst nicht zifferngültig (`ALL` statt `00`), damit er nie mit einem echten
 * PLZ-Präfix kollidiert — `{@link ServiceAreaSchema}` lässt nur zwei Ziffern zu, der
 * Sentinel taucht deshalb in keiner API-Antwort als Gebiet auf.
 */
export const NATIONWIDE_AREA_KEY = 'ALL';
/**
 * Liefergebiete als Ganzes. Wird immer vollständig geschrieben, nie inkrementell:
 * Der Handler bildet die Differenz zum Bestand und löscht, was fehlt. Ein „Gebiet
 * hinzufügen"-Endpunkt hätte keine Antwort auf „und wie wird man eines wieder los".
 *
 * `nationwide` und `serviceAreas` schließen sich **nicht** aus, und das ist der Punkt: Ein
 * Betrieb, der überall liefert, aber für 8xxx einen Anfahrtszuschlag ansetzt, trägt beides
 * ein. Die Gebietsliste ist dann eine Preisliste, keine Einschränkung.
 */
export const ServiceAreasUpdateSchema = z.object({
    /**
     * Deutschlandweit tätig. Der Standard bei der Registrierung — wer sich einschränken will,
     * schaltet ihn ab und trägt seine PLZ-Regionen ein.
     */
    nationwide: z.boolean().default(false),
    serviceAreas: z
        .array(ServiceAreaSchema)
        .max(MAX_SERVICE_AREAS, `Höchstens ${MAX_SERVICE_AREAS} Liefergebiete`)
        .refine((areas) => new Set(areas.map((area) => area.plz2)).size === areas.length, 'Jedes PLZ-Gebiet darf nur einmal vorkommen')
        .default([]),
});
/* --------------------------------------------------------------------------------------------
 * Vorlaufzeit
 * ----------------------------------------------------------------------------------------- */
/**
 * Obergrenze der Vorlaufzeit in Tagen — 90 Tage, also ein Quartal.
 *
 * Wer mehr braucht, betreibt kein Catering-Geschäft, sondern nimmt an Ausschreibungen nicht
 * teil. Die Grenze verhindert vor allem den Zahlendreher: `900` statt `90` wäre ein Betrieb,
 * der nie wieder eine Anfrage bekommt, ohne dass irgendwo ein Fehler aufliefe.
 */
export const LEAD_TIME_MAX_DAYS = 90;
/**
 * Gespeicherte Stunden → Tage für die Oberfläche.
 *
 * **Aufgerundet, nicht kaufmännisch:** Ein Bestand von 36 Stunden ist „zwei Tage" und nicht
 * „ein Tag". Abrunden würde die vom Betrieb genannte Vorlaufzeit beim nächsten Speichern
 * still verkürzen — und ihm eine Anfrage zuspielen, die er nicht bedienen kann.
 */
export function leadTimeDaysFromHours(hours) {
    if (!Number.isFinite(hours) || hours <= 0)
        return 0;
    return Math.min(LEAD_TIME_MAX_DAYS, Math.ceil(hours / 24));
}
/** Tage aus dem Formular → gespeicherte Stunden. */
export function leadTimeHoursFromDays(days) {
    if (!Number.isFinite(days) || days <= 0)
        return 0;
    return Math.min(LEAD_TIME_MAX_DAYS, Math.floor(days)) * 24;
}
/* --------------------------------------------------------------------------------------------
 * Web-Adresse
 * ----------------------------------------------------------------------------------------- */
/**
 * Normalisiert, was ein Gastronom tatsächlich eintippt, zu einer gültigen URL.
 *
 * **Warum das hier steht und nicht als `z.url()` im Feld:** `z.url()` verlangt ein Schema.
 * Wer „www.catering.de" oder „catering.de" einträgt — und das ist der Regelfall, nicht die
 * Ausnahme — bekam eine Formularabweisung für eine Adresse, die jeder Browser auflöst. Der
 * Nutzer sieht seine korrekte Website und eine Fehlermeldung; das ist die Sorte Hürde, an der
 * eine Registrierung abbricht.
 *
 * Ergänzt wird deshalb `https://`, nicht `http://`: Ein Link, den wir Kunden im
 * Angebotsvergleich anzeigen, soll verschlüsselt sein. Wer ausdrücklich `http://` einträgt,
 * behält es — das ist seine Entscheidung über seine eigene Seite.
 *
 * Gibt `null` zurück für „leer" **und** für „nicht auflösbar" — die Unterscheidung trifft
 * {@link WebsiteUrlSchema}, das Leeres durchlässt und Unsinn abweist.
 */
export function normalizeWebsiteUrl(raw) {
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    // Nur `http`/`https` dürfen davor stehen. Ohne diese Prüfung würde ein `javascript:`-Präfix
    // unangetastet durchgereicht und landete als Link im Angebotsvergleich.
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
    if (hasScheme && !/^https?:\/\//i.test(trimmed))
        return null;
    let url;
    try {
        url = new URL(hasScheme ? trimmed : `https://${trimmed}`);
    }
    catch {
        return null;
    }
    // Ein Hostname ohne Punkt ist kein Domainname, sondern ein Tippfehler („catering").
    if (!url.hostname.includes('.') || url.hostname.endsWith('.'))
        return null;
    const normalized = url.toString();
    // `new URL('https://catering.de')` liefert „https://catering.de/" — der Schrägstrich am Ende
    // ist technisch korrekt und sieht im Profil trotzdem nach Fehler aus.
    return url.pathname === '/' && url.search === '' && url.hash === '' ? normalized.replace(/\/$/, '') : normalized;
}
/**
 * Das Formularfeld „Website": nimmt entgegen, was Menschen eintippen, und speichert eine URL.
 *
 * Leer ⇒ `null`. Die Normalisierung passiert beim Parsen, also **einmal serverseitig** — was in
 * DynamoDB landet, ist damit immer eine vollständige URL, unabhängig davon, welcher Client
 * geschrieben hat.
 */
export const WebsiteUrlSchema = z
    .string()
    .max(500)
    /*
     * Geprüft wird **vor** der Umwandlung, nicht danach. Sonst wären „leer" und „Unsinn"
     * hinterher beide `null` und nicht mehr auseinanderzuhalten — ein Tippfehler würde
     * stillschweigend als „keine Website" gespeichert, statt das Feld rot zu markieren.
     */
    .refine((raw) => raw.trim() === '' || normalizeWebsiteUrl(raw) !== null, 'Das sieht nicht nach einer Web-Adresse aus. Zum Beispiel: catering.de')
    .transform((raw) => normalizeWebsiteUrl(raw));
/**
 * Postanschrift. Nur intern und für die Rechnung — nie Teil einer öffentlichen Sicht.
 *
 * Die Meldungen sind ausformuliert und deutsch, weil sie **im Formular am Feld landen**
 * (D14). Zods Standardtext („Too small: expected string to have >=1 characters") beantwortet
 * die Frage „was soll ich hier eintragen" nicht.
 */
export const PostalAddressSchema = z.object({
    street: z.string().min(1, 'Bitte Straße und Hausnummer angeben.').max(200),
    postalCode: z.string().regex(/^\d{5}$/, 'Die Postleitzahl besteht aus fünf Ziffern.'),
    city: z.string().min(1, 'Bitte den Ort angeben.').max(120),
    country: z.string().length(2).default('DE'),
});
/**
 * Hinterlegte Nachweisdokumente. Zeiger nach S3, nie der Inhalt selbst — ein Gewerbeschein
 * als Base64 im DynamoDB-Item reißt das 400-KB-Limit und ist außerdem nicht zugriffsgeprüft
 * auslieferbar. Die Auslieferung läuft über vorsignierte URLs mit kurzer Laufzeit.
 */
export const SupplierDocumentsSchema = z.object({
    /** Handelsregisterauszug oder Gewerbeanmeldung. */
    tradeRegisterS3Key: z.string().max(1024).nullable().default(null),
    /** Nachweis Lebensmittelhygiene (HACCP-Konzept, Belehrung nach § 43 IfSG). */
    foodHygieneS3Key: z.string().max(1024).nullable().default(null),
    /** Betriebshaftpflicht — kein Freischaltkriterium, aber im Streitfall die erste Frage. */
    liabilityInsuranceS3Key: z.string().max(1024).nullable().default(null),
});
/**
 * Nachweise, die vor der Freischaltung vorliegen müssen.
 * Hintergrund: Lebensmittel-Haftung, siehe `PLAN.md` §8.
 *
 * Jeder `*VerifiedAt`-Zeitstempel wird **ausschließlich vom Admin** gesetzt (`/admin/*`).
 * Deshalb steht `compliance` nicht in {@link SupplierProfileInputSchema}: Ein Lieferant, der
 * sein eigenes `foodHygieneVerifiedAt` schreiben kann, hat die Prüfung abgeschafft.
 */
export const SupplierComplianceSchema = z.object({
    /** Handelsregister- oder Gewerbenachweis liegt vor. */
    tradeRegisterVerifiedAt: z.iso.datetime().nullable().default(null),
    /** USt-IdNr., gegen VIES geprüft. */
    vatId: z.string().max(20).nullable().default(null),
    vatIdVerifiedAt: z.iso.datetime().nullable().default(null),
    /** Steuernummer — für Kleinunternehmer ohne USt-IdNr. der einzige Beleg. */
    taxNumber: z.string().max(30).nullable().default(null),
    /** Nachweis Lebensmittelhygiene (HACCP / Belehrung nach § 43 IfSG). */
    foodHygieneVerifiedAt: z.iso.datetime().nullable().default(null),
    documents: SupplierDocumentsSchema.default({
        tradeRegisterS3Key: null,
        foodHygieneS3Key: null,
        liabilityInsuranceS3Key: null,
    }),
    /** Interne Prüfnotiz. **Nie** Teil einer Lieferanten- oder Kundensicht. */
    reviewNotes: z.string().max(4000).default(''),
});
/** Die drei Prüfpunkte, ohne die nicht freigeschaltet wird. Reihenfolge = Reihenfolge in der UI. */
export const COMPLIANCE_CHECKS = [
    { key: 'tradeRegisterVerifiedAt', label: 'Handelsregister oder Gewerbeanmeldung' },
    { key: 'vatIdVerifiedAt', label: 'USt-IdNr. gegen VIES geprüft' },
    { key: 'foodHygieneVerifiedAt', label: 'Nachweis Lebensmittelhygiene' },
];
/**
 * Rechnungsdaten für die Provisionsrechnung (D19 — caterinx fakturiert an den Lieferanten).
 *
 * `billingEmail` und `billingAddress` sind getrennt von den Kontaktdaten, weil die Buchhaltung
 * regelmäßig woanders sitzt als die Küche. `null` heißt hier ausdrücklich „nimm die
 * Kontaktdaten" und nicht „unbekannt" — so bleibt der Regelfall ein leeres Formular.
 */
export const SupplierBillingSchema = z.object({
    billingEmail: z.email().nullable().default(null),
    billingAddress: PostalAddressSchema.nullable().default(null),
    /** Abweichender Rechnungsempfänger, z. B. eine Steuerkanzlei. */
    billingName: z.string().max(200).nullable().default(null),
});
/**
 * Sperrtermine — Tage, an denen der Betrieb grundsätzlich nicht liefert (Betriebsferien,
 * Feiertage, ausgebuchte Großveranstaltungen).
 *
 * Als Datumsliste und nicht als Kalendersystem: Das MVP hat bewusst keine Kapazitäts-
 * synchronisation (`PLAN.md` §6). Ein Hard-Filter „Eventdatum steht in der Sperrliste" kostet
 * nichts und verhindert die peinlichste Variante des Fehlmatchings.
 */
export const BLACKOUT_DATE_LIMIT = 120;
export const SupplierAvailabilitySchema = z.object({
    blackoutDates: z
        .array(z.iso.date())
        .max(BLACKOUT_DATE_LIMIT)
        .default([])
        .refine((dates) => new Set(dates).size === dates.length, 'Jeder Sperrtermin darf nur einmal vorkommen'),
});
/** Vollständiger Stammdatensatz — nur für den Lieferanten selbst und für Admins. */
export const SupplierProfileSchema = z.object({
    supplierId: z.string().min(1),
    companyName: z.string().min(2).max(200),
    status: SupplierStatusSchema,
    // Kontakt — nach D17 erst nach Zuschlag für den Kunden sichtbar.
    contactName: z.string().min(1).max(200),
    contactEmail: z.email(),
    contactPhone: z.string().max(40).nullable().default(null),
    address: PostalAddressSchema,
    // Profil — vor dem Zuschlag sichtbar.
    description: z.string().max(4000).default(''),
    logoS3Key: z.string().max(1024).nullable().default(null),
    referenceImageS3Keys: z.array(z.string().max(1024)).max(8).default([]),
    websiteUrl: z.url().nullable().default(null),
    serviceTypes: z.array(ServiceTypeSchema).default([]),
    cuisines: z.array(CuisineSchema).default([]),
    dietaryOptions: z.array(DietaryOptionSchema).default([]),
    certifications: z.array(SupplierCertificationSchema).default([]),
    serviceAreas: z.array(ServiceAreaSchema).default([]),
    /** Liefert deutschlandweit (Sentinel-Gebiet `AREA#ALL`). Standard bei der Registrierung. */
    nationwide: z.boolean().default(false),
    minOrderNetCents: z.int().min(0).default(0),
    minGuests: z.int().min(0).default(0),
    maxGuests: z.int().min(0).nullable().default(null),
    /**
     * Vorlaufzeit, die der Lieferant mindestens braucht. Hard-Filter im Matching.
     *
     * **Gespeichert in Stunden, erfasst in Tagen** ({@link LEAD_TIME_MAX_DAYS}). Die Stunde ist
     * die richtige Einheit zum Rechnen: Das Matching vergleicht gegen den echten
     * Veranstaltungsbeginn, und „übermorgen 8 Uhr" ist etwas anderes als „übermorgen 20 Uhr".
     * Ein Gastronom denkt aber in Tagen, nicht in 72 Stunden — die Umrechnung macht deshalb die
     * Oberfläche über {@link leadTimeDaysFromHours} / {@link leadTimeHoursFromDays}.
     */
    leadTimeHours: z
        .int()
        .min(0)
        .max(LEAD_TIME_MAX_DAYS * 24)
        .default(24),
    availability: SupplierAvailabilitySchema.default({ blackoutDates: [] }),
    billing: SupplierBillingSchema.default({ billingEmail: null, billingAddress: null, billingName: null }),
    compliance: SupplierComplianceSchema,
    /**
     * Abweichender Provisionssatz in Basispunkten. `null` = Plattform-Default aus SSM.
     * Einführungsrabatt für die ersten Partner (D16).
     */
    commissionRateBp: z.int().min(0).max(10000).nullable().default(null),
    /**
     * Gesetzt, solange eine Provisionsrechnung überfällig ist (Phase 6). Ein gesperrter
     * Betrieb bekommt **keine neuen Einladungen** — der Hard-Filter im Matching liest dieses
     * Feld.
     *
     * Bewusst kein Statuswechsel auf `suspended`: Eine offene Rechnung ist kein
     * Verifizierungsmangel. Der Betrieb bleibt freigeschaltet, sein Profil bleibt sichtbar,
     * laufende Ausschreibungen laufen weiter — er bekommt nur nichts Neues, bis bezahlt ist.
     * Wer beides in dasselbe Feld schreibt, kann hinterher nicht mehr sagen, warum jemand
     * gesperrt war.
     */
    billingBlockedAt: z.iso.datetime().nullable().default(null),
    /** Warum gesperrt — steht dem Lieferanten im Portal, damit er weiß, was zu tun ist. */
    billingBlockReason: z.string().max(500).nullable().default(null),
    /** Grund der letzten Ablehnung oder Sperre. Wird dem Lieferanten im Portal gezeigt. */
    statusReason: z.string().max(1000).nullable().default(null),
    statusChangedAt: z.iso.datetime().nullable().default(null),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    verifiedAt: z.iso.datetime().nullable().default(null),
});
/* --------------------------------------------------------------------------------------------
 * Eingaben
 * ----------------------------------------------------------------------------------------- */
/**
 * Was ein Lieferant an seinem eigenen Profil ändern darf.
 *
 * Einschlussliste, kein `.omit()` — aus demselben Grund wie bei den Sichten: Ein neu
 * ergänztes Feld auf dem Stammdatensatz ist damit per Default **nicht** schreibbar. Eine
 * Ausschlussliste hätte den umgekehrten Default, und die erste Ergänzung, die jemand
 * vergisst, wäre ein Lieferant, der sich selbst auf `verified` setzt.
 *
 * `.partial()` liegt bewusst darüber: Der Onboarding-Wizard speichert nach **jedem** Schritt
 * (die meisten Gastronomen sitzen am Handy, und ein verlorenes Formular kommt nicht wieder),
 * schickt also immer nur einen Ausschnitt.
 */
export const SupplierProfileInputSchema = z
    .object({
    companyName: z.string().min(2, 'Bitte den Firmennamen angeben (mindestens zwei Zeichen).').max(200),
    contactName: z.string().min(1, 'Bitte einen Ansprechpartner angeben.').max(200),
    contactPhone: z.string().max(40, 'Die Telefonnummer ist zu lang.').nullable(),
    address: PostalAddressSchema,
    description: z.string().max(4000, 'Die Kurzbeschreibung darf höchstens 4000 Zeichen haben.'),
    logoS3Key: z.string().max(1024).nullable(),
    referenceImageS3Keys: z.array(z.string().max(1024)).max(8),
    /**
     * Nimmt „catering.de" und „www.catering.de" an — normalisiert wird beim Parsen.
     *
     * `.nullable()` und **kein** `z.union([…, z.null()])`: Eine fehlgeschlagene Union meldet
     * nur „Invalid input" und verschluckt die eigentliche Begründung. Genau die soll das
     * Formular am Feld anzeigen.
     */
    websiteUrl: WebsiteUrlSchema.nullable(),
    serviceTypes: z.array(ServiceTypeSchema).max(ServiceTypeSchema.options.length),
    cuisines: z.array(CuisineSchema).max(CuisineSchema.options.length),
    dietaryOptions: z.array(DietaryOptionSchema).max(DietaryOptionSchema.options.length),
    certifications: z.array(SupplierCertificationSchema).max(SupplierCertificationSchema.options.length),
    minOrderNetCents: z.int().min(0, 'Der Mindestbestellwert kann nicht negativ sein.').max(100_000_000),
    minGuests: z.int().min(0, 'Die Gästezahl kann nicht negativ sein.').max(100_000, 'Das sind zu viele Gäste.'),
    maxGuests: z
        .int()
        .min(0, 'Die Gästezahl kann nicht negativ sein.')
        .max(100_000, 'Das sind zu viele Gäste.')
        .nullable(),
    leadTimeHours: z
        .int()
        .min(0, 'Die Vorlaufzeit kann nicht negativ sein.')
        .max(LEAD_TIME_MAX_DAYS * 24, `Die Vorlaufzeit liegt bei höchstens ${LEAD_TIME_MAX_DAYS} Tagen.`),
    availability: SupplierAvailabilitySchema,
    billing: SupplierBillingSchema,
})
    .partial()
    .refine((input) => input.maxGuests == null || input.minGuests == null || input.maxGuests >= input.minGuests, 
// Der Pfad hängt die Meldung an **das Feld, das der Nutzer ändern wird**. Ohne ihn wäre
// sie ein formularweiter Fehler ohne Bezug, und im Wizard bliebe unklar, welche der
// beiden Zahlen falsch ist.
{ error: 'Die maximale Gästezahl darf nicht unter der minimalen liegen.', path: ['maxGuests'] });
/**
 * **Handanlage durch den Admin** — der Weg, über den die ersten zehn Partner reinkommen
 * (`docs/05_IMPLEMENTATION_STEPS.md`, Phase 3). Bewusst minimal: Was der Admin am Telefon
 * erfährt, ist der Firmenname und eine E-Mail-Adresse. Alles andere trägt der Betrieb
 * selbst nach, sonst wird aus der Akquise Dateneingabe.
 */
export const SupplierAdminCreateSchema = z.object({
    companyName: z.string().min(2).max(200),
    contactName: z.string().min(1).max(200),
    contactEmail: z.email(),
    contactPhone: z.string().max(40).nullable().optional(),
    /** Optionale Notiz für die spätere Prüfung („kam über Messe Düsseldorf"). */
    reviewNotes: z.string().max(4000).optional(),
    /** Abweichender Provisionssatz, z. B. als Einführungsrabatt (D16). */
    commissionRateBp: z.int().min(0).max(10000).nullable().optional(),
});
/** Statuswechsel durch den Admin. Der Grund ist bei Ablehnung und Sperre Pflicht. */
export const SupplierStatusUpdateSchema = z
    .object({
    status: SupplierStatusSchema,
    reason: z.string().max(1000).optional(),
})
    .refine((input) => !['rejected', 'suspended'].includes(input.status) || (input.reason?.trim().length ?? 0) >= 10, 'Ablehnung und Sperre brauchen eine Begründung von mindestens 10 Zeichen.');
/**
 * Prüfergebnis durch den Admin.
 *
 * Die Zeitstempel werden **nicht** vom Client geliefert, sondern als Booleans gesetzt: Der
 * Server stempelt selbst. Sonst könnte ein Admin-Client versehentlich eine Prüfung auf ein
 * Datum in der Vergangenheit datieren, und die Nachvollziehbarkeit wäre dahin.
 */
export const SupplierComplianceUpdateSchema = z.object({
    tradeRegisterVerified: z.boolean().optional(),
    vatId: z.string().max(20).nullable().optional(),
    vatIdVerified: z.boolean().optional(),
    taxNumber: z.string().max(30).nullable().optional(),
    foodHygieneVerified: z.boolean().optional(),
    reviewNotes: z.string().max(4000).optional(),
    commissionRateBp: z.int().min(0).max(10000).nullable().optional(),
});
/* --------------------------------------------------------------------------------------------
 * Sichten
 * ----------------------------------------------------------------------------------------- */
/**
 * Was ein Kunde vor dem Zuschlag sehen darf.
 *
 * Bewusst als eigenständiges Schema aufgebaut, nicht als `.omit()` auf dem Stammdatensatz:
 * ein `.omit()` wäre eine Ausschlussliste, und Ausschlusslisten veralten still, sobald jemand
 * ein Feld ergänzt. Hier ist es eine Einschlussliste — neue Felder sind per Default unsichtbar.
 */
export const SupplierPublicViewSchema = z.object({
    supplierId: z.string().min(1),
    companyName: z.string().min(2).max(200),
    description: z.string().max(4000),
    logoS3Key: z.string().nullable(),
    serviceTypes: z.array(ServiceTypeSchema),
    cuisines: z.array(CuisineSchema),
    dietaryOptions: z.array(DietaryOptionSchema),
    certifications: z.array(SupplierCertificationSchema),
    websiteUrl: z.url().nullable(),
    /** Nur Stadt, keine Straße — die Anschrift ist kein öffentliches Feld. */
    city: z.string().min(1).max(120),
    verifiedAt: z.iso.datetime().nullable(),
});
/** Zusätzliche Felder, die der Kunde **nach** dem Zuschlag bekommt (Gewinner-Sicht). */
export const SupplierContactViewSchema = SupplierPublicViewSchema.extend({
    contactName: z.string().min(1).max(200),
    contactEmail: z.email(),
    contactPhone: z.string().max(40).nullable(),
    address: PostalAddressSchema,
});
/**
 * Zeile in der Admin-Arbeitsliste.
 *
 * Eigene Sicht statt des vollen Datensatzes, weil die Liste bis zu 50 Betriebe auf einmal
 * lädt — Beschreibungstexte, Sperrtermine und Rechnungsdaten wären dort reine
 * Übertragungslast.
 */
export const SupplierAdminListItemSchema = z.object({
    supplierId: z.string().min(1),
    companyName: z.string(),
    status: SupplierStatusSchema,
    contactName: z.string(),
    contactEmail: z.string(),
    city: z.string(),
    serviceAreaCount: z.int().min(0),
    /** Liefert deutschlandweit — sonst stünde in der Liste „0 Gebiete" für den Regelfall. */
    nationwide: z.boolean().default(false),
    serviceTypes: z.array(ServiceTypeSchema),
    /** Wie viele der drei Pflichtnachweise vorliegen — die eigentliche Arbeitsanzeige. */
    complianceComplete: z.int().min(0),
    createdAt: z.iso.datetime(),
    verifiedAt: z.iso.datetime().nullable(),
});
/* --------------------------------------------------------------------------------------------
 * Team
 * ----------------------------------------------------------------------------------------- */
/** Ein Mitarbeiter eines Lieferanten. Rollen siehe `roles.ts`. */
export const SupplierTeamMemberSchema = z.object({
    userId: z.string().min(1),
    email: z.email(),
    name: z.string().max(200),
    role: z.enum(['owner', 'admin', 'member']),
    status: z.enum(['active', 'invited', 'removed']),
    createdAt: z.iso.datetime(),
});
/**
 * Einladung eines Kollegen.
 *
 * `owner` fehlt in der Rollenauswahl mit Absicht: Es gibt genau einen Inhaber, und er entsteht
 * beim Anlegen des Betriebs. Eine Einladung, die einen zweiten Inhaber erzeugt, wäre ein Weg,
 * sich selbst aus dem eigenen Konto auszusperren.
 */
export const TeamInviteInputSchema = z.object({
    email: z.email(),
    name: z.string().max(200).optional(),
    role: z.enum(['admin', 'member']).default('member'),
});
/** Gültigkeit einer Einladung in Tagen. Trägt als **einziges** Supplier-Item ein TTL (§4.4). */
export const INVITE_TTL_DAYS = 14;
/**
 * Was die öffentliche Einladungsseite sehen darf, bevor jemand angemeldet ist.
 *
 * **Die E-Mail-Adresse steht hier im Klartext, und das ist eine Korrektur.** Die erste Fassung
 * hat sie maskiert (`o***@firma.de`) und den Eingeladenen sie erneut eintippen lassen — mit der
 * Begründung, ein geratener Token dürfe keinen Adressbestand ergeben.
 *
 * Die Begründung trägt nicht: Ein Token hat 128 Bit Entropie, es wird nicht geraten. Und wer
 * einen gültigen Token hat, hat die Einladungsmail — die **an genau diese Adresse** ging. Die
 * Maskierung schützte also nichts, kostete aber einen echten Fehlerfall: Ein Tippfehler wurde
 * serverseitig mit „Die Einladung gilt für eine andere E-Mail-Adresse" abgelehnt, während der
 * Nutzer auf dem Bildschirm nur Sternchen sah und den Fehler nicht finden konnte.
 *
 * Die Adresse wird deshalb ausgefüllt und **schreibgeschützt** angezeigt. Die serverseitige
 * Prüfung in `preSignUp` bleibt unverändert — sie ist der eigentliche Schutz und muss auch
 * greifen, wenn jemand direkt gegen die Cognito-API registriert.
 */
export const InvitePreviewSchema = z.object({
    companyName: z.string(),
    role: z.enum(['owner', 'admin', 'member']),
    email: z.email(),
    expiresAt: z.iso.datetime(),
});
export function missingProfileFields(profile) {
    const gaps = [];
    const push = (field, label) => gaps.push({ field, label });
    if ((profile.companyName ?? '').trim().length < 2)
        push('companyName', 'Firmenname');
    if ((profile.contactName ?? '').trim().length < 1)
        push('contactName', 'Ansprechpartner');
    if ((profile.contactEmail ?? '').trim().length < 1)
        push('contactEmail', 'E-Mail-Adresse');
    if (!profile.address?.street || !profile.address.postalCode || !profile.address.city) {
        push('address', 'Anschrift des Betriebs');
    }
    /**
     * **Die Kurzbeschreibung steht hier bewusst nicht mehr.**
     *
     * Sie war Pflicht mit 40 Zeichen Mindestlänge — und damit die Stelle, an der ein
     * Gastronom im Onboarding hängen blieb: Wer zwischen zwei Schichten sein Profil anlegt,
     * hat keinen Werbetext parat und bricht ab. Ein Betrieb ohne Beschreibung ist
     * ausschreibungsfähig; er verkauft sich im Angebotsvergleich nur schlechter, und das ist
     * seine Entscheidung. Der Wizard weist weiterhin darauf hin — er blockiert nur nicht mehr.
     */
    if ((profile.serviceTypes ?? []).length === 0)
        push('serviceTypes', 'Mindestens eine Leistungsart');
    // Deutschlandweit ist ein vollwertiges Liefergebiet — und seit der Registrierung der Standard.
    if (profile.nationwide !== true && (profile.serviceAreas ?? []).length === 0) {
        push('serviceAreas', 'Liefergebiet — deutschlandweit oder einzelne PLZ-Regionen');
    }
    return gaps;
}
/** Wie viele der drei Pflichtnachweise vorliegen (0–3). Treibt die Admin-Arbeitsliste. */
export function complianceCompleteCount(compliance) {
    if (!compliance)
        return 0;
    return COMPLIANCE_CHECKS.filter((check) => compliance[check.key] != null).length;
}
/** Alle drei Nachweise liegen vor. Vorbedingung für `verified`. */
export function isComplianceComplete(compliance) {
    return complianceCompleteCount(compliance) === COMPLIANCE_CHECKS.length;
}
/* --------------------------------------------------------------------------------------------
 * Beschriftungen
 *
 * Deutsch, an einer Stelle (D14). Portal und Admin-App zeigen dieselben Begriffe — sonst heißt
 * dieselbe Leistung im Lieferantenportal „Business-Lunch" und in der Admin-Liste `business_lunch`.
 * ----------------------------------------------------------------------------------------- */
export const SERVICE_TYPE_LABELS = {
    fingerfood: 'Fingerfood',
    buffet: 'Buffet',
    menue_serviert: 'Menü am Platz serviert',
    business_lunch: 'Business-Lunch',
    fruehstueck: 'Frühstück',
    kaffeepause: 'Kaffeepause und Gebäck',
    getraenke: 'Getränke',
    foodtruck: 'Foodtruck',
    grill: 'Grill und BBQ',
    personal_service: 'Servicepersonal',
};
export const CUISINE_LABELS = {
    deutsch: 'Deutsch',
    international: 'International',
    italienisch: 'Italienisch',
    franzoesisch: 'Französisch',
    mediterran: 'Mediterran',
    asiatisch: 'Asiatisch',
    orientalisch: 'Orientalisch',
    indisch: 'Indisch',
    amerikanisch: 'Amerikanisch',
    levantinisch: 'Levantinisch',
};
export const DIETARY_OPTION_LABELS = {
    vegetarisch: 'Vegetarisch',
    vegan: 'Vegan',
    glutenfrei: 'Glutenfrei',
    laktosefrei: 'Laktosefrei',
    halal: 'Halal',
    koscher: 'Koscher',
    nussfrei: 'Nussfrei',
    schweinefleischfrei: 'Ohne Schweinefleisch',
};
export const CERTIFICATION_LABELS = {
    bio_eu: 'EU-Bio-Zertifizierung',
    haccp: 'HACCP-Konzept',
    ifs_food: 'IFS Food',
    iso_22000: 'ISO 22000',
    msc: 'MSC (nachhaltiger Fisch)',
    demeter: 'Demeter',
    regional: 'Regionale Beschaffung',
    klimaneutral: 'Klimaneutraler Betrieb',
};
export const SUPPLIER_STATUS_LABELS = {
    pending: 'Neu — Prüfung offen',
    in_review: 'In Prüfung',
    verified: 'Freigeschaltet',
    suspended: 'Gesperrt',
    rejected: 'Abgelehnt',
};
